import type { Address, CurrentUser } from '@arbuz/shared';
import { makeT } from '../i18n';
import { haptic } from '../tg';
import { BottomAction, ClientTopBar, IconCircle, ThemedIcon, type ThemedIconName } from './clientUi';

export function Catalog({
  user,
  address,
  onCheckout,
  onOrders,
}: {
  user: CurrentUser;
  address: Address | null;
  onCheckout: () => void;
  onOrders: () => void;
}) {
  const t = makeT(user.locale);
  const location = address?.address_text ?? t('addressTitle');

  return (
    <main className="client-page with-bottom-bar" style={{ paddingBottom: 176 }}>
      <ClientTopBar
        location={location}
        right={
          <button type="button" className="client-icon-button" aria-label={t('orders')} onClick={onOrders}>
            <ThemedIcon name="orders" />
          </button>
        }
      />

      <section className="client-home-hero">
        <div className="client-home-copy">
          <h1 className="h1">{t('homeHeroTitle')}</h1>
          <p className="muted" style={{ marginTop: 'var(--s-4)' }}>
            {t('homeHeroText')}
          </p>
        </div>
      </section>

      <section className="client-feature-grid" aria-label={t('homeBenefits')}>
        <Feature image="/assets/watermelon-quality-icon.png" title={t('benefitQuality')} text={t('benefitQualityText')} />
        <Feature icon="delivery" title={t('benefitDelivery')} text={t('benefitDeliveryText')} />
        <Feature icon="order" title={t('benefitOrder')} text={t('benefitOrderText')} />
        <Feature icon="phone" title={t('benefitContact')} text={t('benefitContactText')} />
      </section>

      <BottomAction className="above-nav">
        <button
          type="button"
          className="btn-primary"
          style={{ width: '100%' }}
          disabled={!user.has_phone}
          onClick={() => {
            haptic('light');
            onCheckout();
          }}
        >
          {t('checkout')}
        </button>
      </BottomAction>

      <nav className="client-nav" aria-label={t('catalog')}>
        <button type="button">
          <b><ThemedIcon name="home" /></b>
          {t('catalogHome')}
        </button>
        <button type="button" onClick={onOrders}>
          <b style={{ color: 'var(--c-text-tertiary)' }}><ThemedIcon name="orders" /></b>
          {t('orders')}
        </button>
        <button type="button" disabled>
          <b style={{ color: 'var(--c-text-tertiary)' }}><ThemedIcon name="favorites" /></b>
          {t('favorites')}
        </button>
        <button type="button" disabled>
          <b style={{ color: 'var(--c-text-tertiary)' }}><ThemedIcon name="profile" /></b>
          {t('profile')}
        </button>
      </nav>
    </main>
  );
}

function Feature({
  icon,
  image,
  title,
  text,
}: {
  icon?: ThemedIconName;
  image?: string;
  title: string;
  text: string;
}) {
  return (
    <article className="client-card client-feature">
      <IconCircle icon={icon}>
        {image ? <img className="client-feature-image-icon" src={image} alt="" aria-hidden="true" /> : null}
      </IconCircle>
      <strong style={{ display: 'block', fontSize: 'var(--fs-sm)', lineHeight: 'var(--lh-sm)' }}>
        {title}
      </strong>
      <p className="muted" style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--fs-xs)' }}>
        {text}
      </p>
    </article>
  );
}

export { QuantityStepper as Stepper } from './clientUi';
